/**
 * Source de vérité unique pour les scores de blocs d'un profil.
 *
 * À partir des votes plats (dishLikes, dishDislikes) et du catalogue,
 * calcule pour chaque bloc :
 *   - exposure (likes / dislikes / total)
 *   - score Wilson signé [-1, +1]
 *   - confidence Wilson [0, 1]
 *
 * Source de vérité unique = profile.blockPreferences.dishLikes /
 * dishDislikes + le catalogue products. Pas de cache Firestore à
 * maintenir, pas de dérive entre signal calculé et signal persisté.
 */

import { computeBlockExposure } from './blockExposure.js';
import { wilsonScore, wilsonConfidence } from './wilsonScore.js';

/**
 * @param {object} profile
 * @param {Array} dishCatalogue
 * @returns {Map<string, { likes, dislikes, total, score, confidence }>}
 */
export function computeEffectiveBlockScores(profile, dishCatalogue) {
  const prefs = profile?.blockPreferences || {};
  const exposure = computeBlockExposure(
    prefs.dishLikes || [],
    prefs.dishDislikes || [],
    dishCatalogue || []
  );

  // Votes directs sur blocs = override hard (l'utilisateur a explicitement
  // confirmé son sentiment sur ce bloc, on lui fait confiance entièrement).
  const directLikes = new Set(prefs.likes || []);
  const directDislikes = new Set(prefs.dislikes || []);

  const out = new Map();
  for (const [blockId, { likes, dislikes }] of exposure) {
    const total = likes + dislikes;
    // Pureté du signal : 1 = unanimité (ex 9/0), 0 = mixte (ex 5/5).
    // Permet de pondérer plus fort les signaux discriminants vs les blocs
    // qui se retrouvent par hasard dans des plats aimés ET détestés.
    const purity = total > 0 ? Math.abs(likes - dislikes) / total : 0;
    out.set(blockId, {
      likes,
      dislikes,
      total,
      score: wilsonScore(likes, dislikes),
      confidence: wilsonConfidence(likes, total),
      purity,
      direct: false
    });
  }

  // Append/override les votes directs (même si le bloc n'a aucune exposure plat)
  for (const blockId of directLikes) {
    out.set(blockId, { likes: 1, dislikes: 0, total: 1, score: 1, confidence: 1, purity: 1, direct: true });
  }
  for (const blockId of directDislikes) {
    out.set(blockId, { likes: 0, dislikes: 1, total: 1, score: -1, confidence: 1, purity: 1, direct: true });
  }

  // Neutralisations manuelles (editeur de gouts) : override dur "aucun
  // signal". Applique en dernier pour gagner sur l'exposure ET sur un vote
  // direct residuel incoherent. confidence 1 : les selecteurs d'exploration
  // considereront le bloc comme connu et ne le reproposeront pas.
  // direct: true fait aussi compter ce bloc comme "couvert" dans le calcul de
  // completion du quiz, ce qui fait monter la progression affichee : voulu
  // (le client a tranche, on ne lui en reparle plus).
  for (const blockId of prefs.neutral || []) {
    out.set(blockId, { likes: 0, dislikes: 0, total: 0, score: 0, confidence: 1, purity: 1, direct: true, neutral: true });
  }

  return out;
}


/**
 * Variante array trié par |score| décroissant. Utile pour UI debug.
 */
export function topBlockScores(profile, dishCatalogue, { limit = 20, sign = null } = {}) {
  const map = computeEffectiveBlockScores(profile, dishCatalogue);
  let arr = Array.from(map.entries()).map(([id, v]) => ({ id, ...v }));
  if (sign === 'positive') arr = arr.filter((x) => x.score > 0);
  else if (sign === 'negative') arr = arr.filter((x) => x.score < 0);
  arr.sort((a, b) => Math.abs(b.score) - Math.abs(a.score));
  if (sign === 'positive') arr.sort((a, b) => b.score - a.score);
  if (sign === 'negative') arr.sort((a, b) => a.score - b.score);
  return arr.slice(0, limit);
}
